import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Download, Loader2, Check, AlertCircle, FolderOpen, Archive } from 'lucide-react';
import { useProject } from '../context/ProjectContext';

// Use backend proxy to avoid CORS issues
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ImportDialog = ({ open, onOpenChange }) => {
  const { createProject, updateProject, selectProject, projects, addImportedProject } = useProject();
  const [apiKey, setApiKey] = useState('');
  const [includeArchived, setIncludeArchived] = useState(true);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('input'); // input, loading, projects, importing, done, error
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, currentName: '' });
  const [error, setError] = useState('');
  const [importedCount, setImportedCount] = useState(0);

  const handleFetchProjects = async () => {
    if (!apiKey.trim()) {
      setError('Bitte geben Sie einen API-Key ein');
      return;
    }

    setLoading(true);
    setError('');
    setStep('loading');

    try {
      // Fetch all projects via backend proxy - include archived option
      const response = await fetch(
        `${API_BASE_URL}/api/import/projects?api_key=${encodeURIComponent(apiKey)}&include_archived=${includeArchived}`, 
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Ungültiger API-Key. Bitte überprüfen Sie Ihren Schlüssel.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `API-Fehler: ${response.status}`);
      }

      const projectsList = await response.json();
      setAvailableProjects(projectsList);
      // Select only non-archived by default
      setSelectedProjects(new Set(projectsList.filter(p => !p.archived).map(p => p.id)));
      setStep('projects');
    } catch (err) {
      setError(err.message || 'Fehler beim Abrufen der Projekte');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const toggleProject = (projectId) => {
    setSelectedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedProjects(new Set(availableProjects.map(p => p.id)));
  };

  const selectNone = () => {
    setSelectedProjects(new Set());
  };

  const handleImport = async () => {
    if (selectedProjects.size === 0) {
      setError('Bitte wählen Sie mindestens ein Projekt aus');
      return;
    }

    setStep('importing');
    setImportProgress({ current: 0, total: selectedProjects.size, currentName: '' });
    setError('');

    const projectsToImport = availableProjects.filter(p => selectedProjects.has(p.id));
    let imported = 0;

    for (const project of projectsToImport) {
      setImportProgress({
        current: imported + 1,
        total: projectsToImport.length,
        currentName: project.name
      });

      try {
        // Export project data via backend proxy
        const response = await fetch(`${API_BASE_URL}/api/import/projects/${project.id}/export?api_key=${encodeURIComponent(apiKey)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error(`Fehler beim Import von ${project.name}: ${response.status}`);
          continue;
        }

        const projectData = await response.json();
        
        // Convert the imported data to our internal format
        const convertedProject = convertStromlaufplanProject(project, projectData);
        
        // Check if project with same name already exists
        const existingProject = projects.find(p => p.name === project.name);
        
        if (existingProject) {
          // Update existing project - pass all properties
          updateProject(existingProject.id, {
            ...convertedProject,
            id: existingProject.id  // Keep the existing ID
          });
        } else {
          // Create and immediately set project with all data
          // Use addImportedProject instead of createProject + updateProject
          addImportedProject(convertedProject);
        }
        
        imported++;
      } catch (err) {
        console.error(`Fehler beim Import von ${project.name}:`, err);
      }
    }

    setImportedCount(imported);
    setStep('done');
  };

  // Convert stromlaufplan.de project format to our internal format
  const convertStromlaufplanProject = (projectMeta, projectData) => {
    // projectData contains: documentData, treeNodes, treeNodeDatas
    const { documentData, treeNodes, treeNodeDatas } = projectData;
    
    // Build a map of node data by ID
    const nodeDataMap = {};
    if (treeNodeDatas && Array.isArray(treeNodeDatas)) {
      treeNodeDatas.forEach(nd => {
        if (nd.nodeID) {
          nodeDataMap[nd.nodeID] = nd.data;
        }
      });
    } else if (treeNodeDatas && typeof treeNodeDatas === 'object') {
      // Handle case where treeNodeDatas is an object with nodeID keys
      Object.entries(treeNodeDatas).forEach(([key, value]) => {
        if (value && value.nodeID) {
          nodeDataMap[value.nodeID] = value.data;
        } else {
          nodeDataMap[key] = value;
        }
      });
    }

    // Convert tree structure
    const convertNode = (apiNode) => {
      const nodeData = nodeDataMap[apiNode.id] || {};
      const nodeType = mapNodeType(apiNode.type);
      
      return {
        id: apiNode.id,
        type: nodeType,
        bezeichnung: apiNode.text || nodeData.bezeichnung || '',
        expanded: false,
        data: extractNodeData(nodeType, nodeData, apiNode),
        children: (apiNode.children || []).map(convertNode)
      };
    };

    const nodes = (treeNodes || []).map(convertNode);

    return {
      name: projectMeta.name || documentData?.projectName || 'Importiertes Projekt',
      address: projectMeta.address || documentData?.projectAddress || '',
      firma: documentData?.companyName || '',
      ersteller: documentData?.createdBy || '',
      zeichnungsnummer: documentData?.drawingNumber || '',
      nodes,
      updatedAt: new Date().toISOString(),
      importedFrom: 'stromlaufplan.de',
      originalId: projectMeta.id
    };
  };

  // Map API node types to our internal types
  const mapNodeType = (apiType) => {
    const typeMap = {
      'T230V': 'einspeisung',
      'T230V_secondary': 'einspeisung',
      'rcd': 'rcd',
      'ocd': 'ls',
      'ls': 'ls',
      'rcd_ls': 'rcbo',
      'out': 'kabel',
      'kleinsp_out': 'kabel',
      'knx_out': 'kabel',
      'zaehl': 'zaehler',
      'ueschutz': 'ueberspannungsschutz',
      'trenn': 'sls',
      'notiz': 'notiz',
      'knx_sa': 'aktor',
      'knx_da': 'aktor',
      'knx_ha': 'aktor',
      'knx_ja': 'aktor',
    };
    return typeMap[apiType] || apiType;
  };

  // Extract relevant data from node based on type
  const extractNodeData = (type, nodeData, apiNode) => {
    switch (type) {
      case 'rcd':
        return {
          hersteller: nodeData.hersteller || '',
          artikelnummer: nodeData.artikelnummer || '',
          nennstrom: nodeData.kennwerte?.split('/')[0] || '63',
          fehlerstrom: nodeData.kennwerte?.split('/')[1]?.replace('mA', '') || '30',
          pole: '4',
          typ: nodeData.rcdSubType || 'A'
        };
      case 'ls':
        return {
          hersteller: nodeData.hersteller || '',
          artikelnummer: nodeData.artikelnummer || '',
          nennstrom: nodeData.kennwerte?.replace(/[A-Z]/g, '') || '16',
          charakteristik: nodeData.kennwerte?.match(/[A-Z]/)?.[0] || 'B',
          pole: nodeData.ocdSubType?.includes('3') ? '3' : '1',
          verbraucher: nodeData.bezeichnung || ''
        };
      case 'rcbo':
        return {
          hersteller: nodeData.hersteller || '',
          artikelnummer: nodeData.artikelnummer || '',
          nennstrom: nodeData.kennwerte?.split('/')[0] || '16',
          fehlerstrom: nodeData.kennwerte?.split('/')[1]?.replace('mA', '') || '30',
          charakteristik: 'B',
          pole: '1+N',
          typ: 'A',
          verbraucher: nodeData.bezeichnung || ''
        };
      case 'kabel':
        return {
          leitungsnr: nodeData.leitungsnummer || '1',
          kabeltyp: nodeData.leitungstyp || 'NYM-J',
          querschnitt: nodeData.querschnitt || '2,5',
          anzahlAdern: nodeData.adern?.length || 3,
          laenge: '',
          klemmleiste: nodeData.klemmleiste || 'X1',
          adern: (nodeData.adern || []).map((ader, idx) => ({
            nr: idx + 1,
            typ: ader.klemmenTyp === 'PE' ? 'PE' : ader.klemmenTyp === 'N' ? 'N' : 'L',
            bezeichnung: ader.klemmenBeschriftung || '',
            zuordnung: 'auto'
          }))
        };
      case 'einspeisung':
        return {
          kabeltyp: nodeData.einspeisungLeitungstyp || 'NYM-J',
          querschnitt: nodeData.einspeisungQuerschnitt || '5x10',
          netzform: 'TN-C-S'
        };
      case 'zaehler':
        return {
          typ: nodeData.zaehlerSubType || 'EHZ',
          messart: 'direkt'
        };
      case 'sls':
        return {
          nennstrom: nodeData.kennwerte?.replace(/[A-Z]/g, '') || '63',
          pole: '3'
        };
      default:
        return nodeData;
    }
  };

  const resetDialog = () => {
    setStep('input');
    setApiKey('');
    setIncludeArchived(true);
    setAvailableProjects([]);
    setSelectedProjects(new Set());
    setError('');
    setImportProgress({ current: 0, total: 0, currentName: '' });
    setImportedCount(0);
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Projekte von stromlaufplan.de importieren
          </DialogTitle>
          <DialogDescription>
            Importieren Sie alle Ihre Projekte von app.stromlaufplan.de mit Ihrem API-Key.
          </DialogDescription>
        </DialogHeader>

        {step === 'input' && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>API-Key</Label>
              <Input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="z.B. 44035544-3d5a-4a4f-99aa-31e88e81e84a"
                className="font-mono"
              />
              <p className="text-xs text-gray-500">
                Den API-Key finden Sie in Ihrem stromlaufplan.de Konto unter "API-Schnittstellen".
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeArchived"
                checked={includeArchived}
                onCheckedChange={setIncludeArchived}
              />
              <Label htmlFor="includeArchived" className="text-sm cursor-pointer">
                Archivierte Projekte einschließen
              </Label>
            </div>
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
          </div>
        )}

        {step === 'loading' && (
          <div className="py-8 flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            <p className="text-gray-600">Projekte werden abgerufen...</p>
          </div>
        )}

        {step === 'projects' && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{availableProjects.length}</span> Projekt(e) gefunden
                {availableProjects.filter(p => p.archived).length > 0 && (
                  <span className="text-gray-400 ml-2">
                    ({availableProjects.filter(p => !p.archived).length} aktiv, {availableProjects.filter(p => p.archived).length} archiviert)
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll}>
                  Alle auswählen
                </Button>
                <Button variant="outline" size="sm" onClick={selectNone}>
                  Keine auswählen
                </Button>
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {availableProjects.map((project) => (
                <div
                  key={project.id}
                  className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                    selectedProjects.has(project.id) ? 'bg-emerald-50' : ''
                  } ${project.archived ? 'opacity-60' : ''}`}
                  onClick={() => toggleProject(project.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedProjects.has(project.id)}
                    onChange={() => {}}
                    className="w-4 h-4 text-emerald-600 rounded"
                  />
                  {project.archived ? (
                    <Archive className="w-4 h-4 text-gray-400" />
                  ) : (
                    <FolderOpen className="w-4 h-4 text-gray-400" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{project.name}</p>
                      {project.archived && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                          Archiviert
                        </span>
                      )}
                    </div>
                    {project.address && (
                      <p className="text-xs text-gray-500 truncate">{project.address}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <p className="text-sm text-gray-600">
              {selectedProjects.size} Projekt(e) zum Import ausgewählt
            </p>
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
          </div>
        )}

        {step === 'importing' && (
          <div className="py-8 space-y-4">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              <p className="text-gray-600">
                Importiere Projekt {importProgress.current} von {importProgress.total}
              </p>
              {importProgress.currentName && (
                <p className="text-sm text-gray-500 truncate max-w-full">
                  {importProgress.currentName}
                </p>
              )}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-emerald-600 h-2 rounded-full transition-all"
                style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="py-8 flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-lg font-medium text-gray-800">Import abgeschlossen!</p>
            <p className="text-gray-600">
              {importedCount} Projekt(e) wurden erfolgreich importiert.
            </p>
          </div>
        )}

        {step === 'error' && (
          <div className="py-8 flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-lg font-medium text-gray-800">Fehler beim Import</p>
            <p className="text-red-600 text-center">{error}</p>
          </div>
        )}

        <DialogFooter>
          {step === 'input' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Abbrechen
              </Button>
              <Button onClick={handleFetchProjects} disabled={!apiKey.trim()}>
                Projekte abrufen
              </Button>
            </>
          )}
          {step === 'projects' && (
            <>
              <Button variant="outline" onClick={() => setStep('input')}>
                Zurück
              </Button>
              <Button onClick={handleImport} disabled={selectedProjects.size === 0}>
                {selectedProjects.size} Projekt(e) importieren
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={handleClose}>
              Schließen
            </Button>
          )}
          {step === 'error' && (
            <>
              <Button variant="outline" onClick={() => setStep('input')}>
                Erneut versuchen
              </Button>
              <Button onClick={handleClose}>
                Schließen
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
